<script>
  import t from "#lib/i18n.svelte.js";
  import Dialog from "#lib/Dialog.svelte";
  import Avatar from "#lib/Avatar.svelte";
  import ButtonLabel from "#lib/ButtonLabel.svelte";
  import ScrollIndicator from "#lib/ScrollIndicator.svelte";
  import { fetch_json, format_date, get_user_slug } from "#lib/util.js";
  import { getContext } from 'svelte';
  import { page } from '$app/state';

  let { 
    /** @type {{ user_id: number, name: string, email?: string, avatar?: any }} */
    user,
    /** @type {() => void} */
    onclose
  } = $props();

  let user_details = $state(user);
  let custom_fields = $state({});
  let representatives = $state([]);
  let delegators = $state([]);
  let site_settings = getContext('site_settings');

  let custom_field_entries = $derived.by(() => {
    const fields = site_settings?.custom_form_fields || [];
    const entries = [];
    // Use configured order first
    for (const field of fields) {
      const value = custom_fields?.[field.id];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        entries.push({ id: field.id, label: field.label || field.id, value });
      }
    }
    // Include any additional keys not in config
    for (const [key, value] of Object.entries(custom_fields || {})) {
      if (!fields.find(f => f.id === key) && value !== undefined && value !== null && String(value).trim() !== '') {
        entries.push({ id: key, label: key, value });
      }
    }
    return entries;
  });

  $effect.pre(() => {
    try {
      custom_fields = typeof user_details?.custom_fields === 'string' 
        ? JSON.parse(user_details.custom_fields) 
        : (user_details?.custom_fields || {});
    } catch {
      custom_fields = {};
    }
  });

  // Enrich with full user data (admins) based on email if available
  $effect.pre(() => {
    if (user?.email) {
      fetch_json('POST', '/api/search-users', { search_query: user.email, limit: 1 })
        .then(result => {
          const u = result?.users?.[0];
          if (u) {
            user_details = { ...user_details, ...u };
            try {
              custom_fields = typeof u.custom_fields === 'string' ? JSON.parse(u.custom_fields) : (u.custom_fields || {});
            } catch {
              custom_fields = {};
            }
            representatives = Array.isArray(u.representatives) ? u.representatives : [];
            delegators = Array.isArray(u.delegators) ? u.delegators : [];
          }
        })
        .catch(console.error);
    }
  });

  function profile_url() {
    return `/${get_user_slug(user_details.user_id, user_details.name)}`;
  }

  function users_search_url() {
    const q = user_details.email || user_details.name || '';
    return `/users?q=${encodeURIComponent(q)}`;
  }
</script>

<Dialog {onclose} class='user-details-dialog wide border-1'>
  <main>
    <header class='flex-wrap items-start gap-x gap-y g-6 justify-between px py p-6'>
      <div class='flex-row items-center gap-x g-4'>
        <Avatar name={user_details?.name} avatar={user_details?.avatar} />
        <div class='flex-column'>
          <p class="text-preamble">{user_details?.email}</p>
          <h3 class="text-heading3">{user_details?.name}</h3>
        </div>
      </div>
      <button onclick={onclose} title={t('close')}>
        <ButtonLabel variant='brand ghostly' start_icon='close' />
      </button>
    </header>

    <section>
      <ScrollIndicator class='px p-1 flex-column'>
        <div class='px py p-6'>
          <div class='key-value-grid'>
            <div class='kv'>
              <div class='k text-control text-3'>{t('name')}</div>
              <div class='v text-control'>{user_details?.name}</div>
            </div>
            <div class='kv'>
              <div class='k text-control text-3'>{t('email')}</div>
              <div class='v text-control'>{user_details?.email}</div>
            </div>
            <div class='kv'>
              <div class='k text-control text-3'>{t('id')}</div>
              <div class='v text-control -tnum'>{user_details?.user_id}</div>
            </div>
            {#if user_details?.role}
              <div class='kv'>
                <div class='k text-control text-3'>{t('role')}</div>
                <div class='v text-control'>{t(user_details.role)}</div>
              </div>
            {/if}
            {#if user_details?.created_at}
              <div class='kv'>
                <div class='k text-control text-3'>{t('registered')}</div>
                <div class='v text-control'>{format_date(user_details.created_at, 'YDM')}</div>
              </div>
            {/if}
            {#if user_details?.is_blocked != null}
              <div class='kv'>
                <div class='k text-control text-3'>{t('blocked')}</div>
                <div class='v text-control'>{user_details.is_blocked ? t('yes') : t('no')}</div>
              </div>
            {/if}
            {#if user_details?.internal_notes && page.data.current_user?.role === 'admin'}
              <div class='kv span-2'>
                <div class='k text-control text-3'>{t('internal_notes')}</div>
                <div class='v text-control'>{user_details.internal_notes}</div>
              </div>
            {/if}

            {#if representatives?.length}
              <div class='kv span-2'>
                <div class='k text-control text-3'>{t('represented_by')}</div>
                <div class='v text-control'>
                  {#each representatives as rep, i}
                    <span>{rep.name}{i < representatives.length - 1 ? ', ' : ''}</span>
                  {/each}
                </div>
              </div>
            {/if}

            {#if delegators?.length}
              <div class='kv span-2'>
                <div class='k text-control text-3'>{t('delegatory')}</div>
                <div class='v text-control'>
                  {#each delegators as del, j}
                    <span>{del.name}{j < delegators.length - 1 ? ', ' : ''}</span>
                  {/each}
                </div>
              </div>
            {/if}

            {#if custom_field_entries.length}
               <hr>
              <div class='kv span-2'>
                <div class='k text-control text-3 -bold'>{t('custom_fields')}</div>
                <div class='v'></div>
              </div>
              {#each custom_field_entries as item}
                <div class='kv'>
                  <div class='k text-control text-3'>{item.label}</div>
                  <div class='v text-control'>{typeof item.value === 'boolean' ? (item.value ? t('yes') : t('no')) : String(item.value ?? '')}</div>
                </div>
              {/each}
            {/if}
          </div>
        </div>
      </ScrollIndicator>
    </section>

    <footer class='flex-row gap-x g-4 justify-end px py p-6'>
      <a href={profile_url()} title='User Profile'>
        <ButtonLabel start_icon='calm-face' variant='primary filled'>{'User Profile'}</ButtonLabel>
      </a>
      <a href={users_search_url()} title='Find in all Users'>
        <ButtonLabel start_icon='search' variant='primary filled'>{'Find in all Users'}</ButtonLabel>
      </a>
    </footer>
  </main>
</Dialog>

<style lang='postcss'>
  main {
    max-height: inherit;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
    grid-template-areas:
      "header"
      "section"
      "footer";
    header {
      grid-area: header;
      border-bottom: 1px solid var(--color-default-stroke);
    }
    section {
      grid-area: section;
      overflow-y: auto;
    }
    footer {
      grid-area: footer;
      border-top: 1px solid var(--color-default-stroke);
    }
  }

  .key-value-grid {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: var(--size-3) var(--size-6);
  }
  .kv {
    display: contents;
  }
  .kv .k {
    align-self: start;
    opacity: 0.8;
  }
  .kv .v {
    align-self: start;
  }
  .kv.span-2 {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
  }

  :global(.user-details-dialog) {
    max-height: min(var(--dialog-max-height), 600px);
  }
</style>

